// =============================================================================
// components/learn/QuizSheet.tsx — end-of-lesson quiz (the "game" gate).
//
// Renders 2-3 questions inline at the foot of an article. Answer all → Check →
// pass (>= PASS_RATIO) unlocks the next lesson and awards XP; fail → retry.
// Purely presentational + records via lib/learnProgress. Light/glass brand.
// =============================================================================
import React, { useMemo, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import { Ionicons } from '../ui/Icon';
import Asset3D from '../ui/Asset3D';
import { colors, fonts, spacing, radius } from '../../constants/theme';
import { quizForArticle, PASS_RATIO, QuizQuestion } from '../../lib/learnQuiz';
import { recordQuiz, isPassed } from '../../lib/learnProgress';

export default function QuizSheet({
  slug,
  title,
  onPassed,
}: {
  slug?: string;
  title?: string;
  onPassed?: (xpAwarded: number) => void;
}) {
  const questions = useMemo<QuizQuestion[]>(() => quizForArticle(slug, title), [slug, title]);
  const alreadyPassed = isPassed(slug);

  const [picks, setPicks] = useState<Record<number, number>>({});
  const [checked, setChecked] = useState(false);
  const [result, setResult] = useState<{ passed: boolean; correct: number; xp: number } | null>(null);

  const allAnswered = Object.keys(picks).length === questions.length;

  async function handleCheck() {
    if (!allAnswered || !slug) return;
    let correct = 0;
    questions.forEach((q, i) => { if (picks[i] === q.answer) correct++; });
    const frac = correct / questions.length;
    const passed = frac >= PASS_RATIO;
    setChecked(true);
    const { xpAwarded } = await recordQuiz(slug, frac, passed);
    setResult({ passed, correct, xp: xpAwarded });
    if (passed) onPassed?.(xpAwarded);
  }

  function reset() {
    setPicks({});
    setChecked(false);
    setResult(null);
  }

  return (
    <View style={styles.wrap}>
      <View style={styles.header}>
        <View style={styles.badge}>
          <Ionicons name="game-controller" size={14} color={colors.primary} />
        </View>
        <View style={{ flex: 1 }}>
          <Text style={styles.kicker}>KNOWLEDGE CHECK</Text>
          <Text style={styles.title}>
            {alreadyPassed ? 'You passed this lesson' : 'Answer to unlock the next lesson'}
          </Text>
        </View>
        {alreadyPassed && (
          <View style={styles.passedPill}>
            <Ionicons name="checkmark-circle" size={14} color={colors.success} />
            <Text style={styles.passedPillText}>Done</Text>
          </View>
        )}
      </View>

      {questions.map((q, qi) => {
        const picked = picks[qi];
        return (
          <View key={qi} style={styles.qBlock}>
            <Text style={styles.qText}>{qi + 1}. {q.q}</Text>
            {q.options.map((opt, oi) => {
              const selected = picked === oi;
              const isCorrect = oi === q.answer;
              // After checking, color right/wrong.
              let tint: 'default' | 'correct' | 'wrong' = 'default';
              if (checked) {
                if (isCorrect) tint = 'correct';
                else if (selected) tint = 'wrong';
              } else if (selected) {
                tint = 'correct'; // pre-check selection just uses the accent style
              }
              return (
                <TouchableOpacity
                  key={oi}
                  activeOpacity={0.8}
                  disabled={checked}
                  onPress={() => setPicks((p) => ({ ...p, [qi]: oi }))}
                  style={[
                    styles.option,
                    selected && !checked && styles.optionSelected,
                    checked && tint === 'correct' && styles.optionCorrect,
                    checked && tint === 'wrong' && styles.optionWrong,
                  ]}
                  accessibilityRole="button"
                  accessibilityState={{ selected }}
                >
                  <View style={[
                    styles.radio,
                    selected && !checked && styles.radioSelected,
                    checked && tint === 'correct' && styles.radioCorrect,
                    checked && tint === 'wrong' && styles.radioWrong,
                  ]}>
                    {checked && tint === 'correct' && <Ionicons name="checkmark" size={12} color="#fff" />}
                    {checked && tint === 'wrong' && <Ionicons name="close" size={12} color="#fff" />}
                    {!checked && selected && <View style={styles.radioDot} />}
                  </View>
                  <Text style={[styles.optionText, checked && isCorrect && styles.optionTextCorrect]}>
                    {opt}
                  </Text>
                </TouchableOpacity>
              );
            })}
            {checked && q.explain && (
              <Text style={styles.explain}>{q.explain}</Text>
            )}
          </View>
        );
      })}

      {/* Celebratory 3D trophy on a pass — the reward beat. */}
      {result?.passed && (
        <View style={{ alignItems: 'center', marginTop: spacing.md }}>
          <Asset3D name="trophy" size={96} />
        </View>
      )}

      {/* Result banner */}
      {result && (
        <View style={[styles.resultBanner, result.passed ? styles.resultPass : styles.resultFail]}>
          <Ionicons
            name={result.passed ? 'trophy' : 'refresh-circle'}
            size={18}
            color={result.passed ? colors.success : colors.error}
          />
          <Text style={styles.resultText}>
            {result.passed
              ? `Passed! ${result.correct}/${questions.length} correct${result.xp ? ` · +${result.xp} XP` : ''}`
              : `${result.correct}/${questions.length} — you need ${Math.ceil(PASS_RATIO * questions.length)} to pass. Try again.`}
          </Text>
        </View>
      )}

      {/* CTA */}
      {!checked ? (
        <TouchableOpacity
          activeOpacity={0.9}
          disabled={!allAnswered}
          onPress={handleCheck}
          accessibilityRole="button"
          accessibilityLabel="Check answers"
        >
          <LinearGradient
            colors={allAnswered ? [...colors.gradient] : ['#D9D2F2', '#D9D2F2']}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            style={[styles.cta, !allAnswered && styles.ctaDisabled]}
          >
            {/* Legible in BOTH states: white on the purple gradient when enabled,
                a clearly-readable purple on a bordered lavender chip when disabled
                (the light-on-light version read as "button not showing"). */}
            <Text style={[styles.ctaText, { color: allAnswered ? '#FFFFFF' : '#5A4B8C' }]} numberOfLines={1}>
              {allAnswered ? 'Check answers' : 'Answer all to check'}
            </Text>
          </LinearGradient>
        </TouchableOpacity>
      ) : result && !result.passed ? (
        <TouchableOpacity style={styles.retryBtn} onPress={reset} activeOpacity={0.8} accessibilityRole="button">
          <Ionicons name="refresh" size={16} color={colors.primary} />
          <Text style={styles.retryText}>Try again</Text>
        </TouchableOpacity>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    marginTop: spacing.xl,
    padding: spacing.lg,
    borderRadius: radius.xl,
    backgroundColor: colors.primary + '0A',
    borderWidth: 1,
    borderColor: colors.primary + '22',
  },
  header: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.md },
  badge: {
    width: 30, height: 30, borderRadius: 15,
    backgroundColor: colors.primary + '18',
    alignItems: 'center', justifyContent: 'center',
  },
  kicker: { ...fonts.bold, fontSize: 10, color: colors.primary, letterSpacing: 1.2 },
  title: { ...fonts.extrabold, fontSize: 15, color: colors.ink, marginTop: 1 },
  passedPill: {
    flexDirection: 'row', alignItems: 'center', gap: 3,
    backgroundColor: colors.success + '18', borderRadius: radius.full,
    paddingHorizontal: spacing.sm, paddingVertical: 3,
  },
  passedPillText: { ...fonts.bold, fontSize: 11, color: colors.success },
  qBlock: { marginTop: spacing.md },
  qText: { ...fonts.bold, fontSize: 14, color: colors.ink, marginBottom: spacing.sm, lineHeight: 20 },
  option: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.sm,
    paddingVertical: spacing.sm + 2, paddingHorizontal: spacing.md,
    borderRadius: radius.md, backgroundColor: '#FFFFFF',
    borderWidth: 1, borderColor: colors.cardBorder, marginBottom: spacing.xs,
  },
  optionSelected: { borderColor: colors.primary, backgroundColor: colors.primary + '0C' },
  optionCorrect: { borderColor: colors.success, backgroundColor: colors.success + '12' },
  optionWrong: { borderColor: colors.error, backgroundColor: colors.error + '10' },
  radio: {
    width: 20, height: 20, borderRadius: 10, borderWidth: 2, borderColor: colors.cardBorder,
    alignItems: 'center', justifyContent: 'center',
  },
  radioSelected: { borderColor: colors.primary },
  radioCorrect: { borderColor: colors.success, backgroundColor: colors.success },
  radioWrong: { borderColor: colors.error, backgroundColor: colors.error },
  radioDot: { width: 8, height: 8, borderRadius: 4, backgroundColor: colors.primary },
  optionText: { ...fonts.medium, fontSize: 13, color: colors.ink2, flex: 1 },
  optionTextCorrect: { color: colors.ink, ...fonts.semibold },
  explain: { ...fonts.regular, fontSize: 12, color: colors.muted, marginTop: 4, lineHeight: 17, fontStyle: 'italic' },
  resultBanner: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.sm,
    marginTop: spacing.md, padding: spacing.md, borderRadius: radius.md,
  },
  resultPass: { backgroundColor: colors.success + '14' },
  resultFail: { backgroundColor: colors.error + '10' },
  resultText: { ...fonts.semibold, fontSize: 13, color: colors.ink, flex: 1 },
  cta: {
    marginTop: spacing.md, borderRadius: radius.full,
    paddingVertical: spacing.md, alignItems: 'center', justifyContent: 'center',
  },
  ctaDisabled: { borderWidth: 1, borderColor: colors.primary + '33' },
  ctaText: { ...fonts.bold, fontSize: 15, color: '#FFFFFF' },
  retryBtn: {
    marginTop: spacing.md, flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: spacing.sm, paddingVertical: spacing.md, borderRadius: radius.full,
    borderWidth: 1, borderColor: colors.primary + '44',
  },
  retryText: { ...fonts.bold, fontSize: 14, color: colors.primary },
});
